
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';

@Component({
    selector: 'app-image-editor',
    templateUrl: './image-editor.component.html',
    styleUrls: ['./image-editor.component.scss'],
    standalone: false
})
export class ImageEditorComponent implements OnInit {

    @Input() imageBase64: string = '';
    @Input() initialElements: any[] = [];

    @ViewChild('canvas') canvasEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('canvasContainer') containerEl!: ElementRef<HTMLDivElement>;

    private ctx!: CanvasRenderingContext2D;
    private img = new Image();

    // Elements storage
    elements: any[] = [];

    // Selection
    // Selection
    selectedElementIndex: number = -1;
    interactionMode: 'none' | 'drawing' | 'dragging' | 'resizing_start' | 'resizing_end' = 'none';

    mode: 'line' | 'text' = 'line';
    textVal: string = '';
    currentColor: string = '#FF0000'; // Default Red

    isDrawing = false;
    startX = 0;
    startY = 0;

    constructor(private modalCtrl: ModalController) { }

    ngOnInit() { }

    ngAfterViewInit() {
        if (this.imageBase64) {
            if (this.initialElements && this.initialElements.length > 0) {
                this.elements = JSON.parse(JSON.stringify(this.initialElements));
            }
            this.loadImage();
        }
    }

    loadImage() {
        this.img.onload = () => {
            this.initCanvas();
        };

        if (this.imageBase64.startsWith('data:image')) {
            this.img.src = this.imageBase64;
        } else {
            this.img.src = 'data:image/jpeg;base64,' + this.imageBase64;
        }
    }

    initCanvas() {
        const canvas = this.canvasEl.nativeElement;
        const container = this.containerEl.nativeElement;
        this.ctx = canvas.getContext('2d')!;

        // Fix: Wait for container to have dimensions (modal animation)
        const maxWidth = container.clientWidth;
        const maxHeight = container.clientHeight;

        if (maxWidth === 0 || maxHeight === 0) {
            setTimeout(() => this.initCanvas(), 100);
            return;
        }

        let w = this.img.width;
        let h = this.img.height;

        const ratio = Math.min(maxWidth / w, maxHeight / h);

        canvas.width = w * ratio;
        canvas.height = h * ratio;

        this.render();

        // Listeners - Interactive
        canvas.addEventListener('mousedown', this.handleStart.bind(this));
        canvas.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });

        canvas.addEventListener('mousemove', this.handleMove.bind(this));
        canvas.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });

        canvas.addEventListener('mouseup', this.handleEnd.bind(this));
        canvas.addEventListener('touchend', this.handleEnd.bind(this));
    }

    render() {
        const cvs = this.canvasEl.nativeElement;
        const w = cvs.width;
        const h = cvs.height;

        // Clear
        this.ctx.clearRect(0, 0, w, h);

        // Draw BG
        this.ctx.drawImage(this.img, 0, 0, w, h);

        // Draw Elements
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        this.elements.forEach((el, index) => {
            const isSelected = (index === this.selectedElementIndex);

            // Denormalize coordinates for rendering
            const x = el.x * w;
            const y = el.y * h;

            this.ctx.globalAlpha = 1.0;

            if (el.type === 'line') {
                const ex = el.ex * w;
                const ey = el.ey * h;

                this.ctx.beginPath();
                this.ctx.moveTo(x, y);
                this.ctx.lineTo(ex, ey);
                this.ctx.strokeStyle = el.color || 'red';
                this.ctx.lineWidth = isSelected ? 5 : 3;
                this.ctx.stroke();

                if (isSelected) {
                    this.drawHandle(x, y);
                    this.drawHandle(ex, ey);

                    // Highlight
                    this.ctx.strokeStyle = 'rgba(255, 255, 0, 0.5)';
                    this.ctx.lineWidth = 1;
                    this.ctx.stroke();
                }
            } else if (el.type === 'text') {
                this.ctx.font = "bold 20px Arial";
                this.ctx.fillStyle = el.color || "red";
                this.ctx.strokeStyle = "white";
                this.ctx.lineWidth = 3;
                this.ctx.strokeText(el.val, x, y);
                this.ctx.fillText(el.val, x, y);

                if (isSelected) {
                    const metrics = this.ctx.measureText(el.val);
                    const textH = 20;
                    this.ctx.strokeStyle = 'blue';
                    this.ctx.lineWidth = 1;
                    this.ctx.strokeRect(x - 2, y - textH, metrics.width + 4, textH + 4);
                }
            }
        });
    }

    drawHandle(x: number, y: number) {
        this.ctx.beginPath();
        this.ctx.fillStyle = 'white';
        this.ctx.strokeStyle = 'blue';
        this.ctx.lineWidth = 2;
        this.ctx.arc(x, y, 8, 0, 2 * Math.PI);
        this.ctx.fill();
        this.ctx.stroke();
    }



    clear() {
        this.saveState();
        this.elements = [];
        this.render();
    }

    modeChanged() { }

    setColor(c: string) {
        this.currentColor = c;
    }

    // Returns PIXEL coordinates for interaction
    getPos(ev: any) {
        const rect = this.canvasEl.nativeElement.getBoundingClientRect();
        let clientX = ev.clientX;
        let clientY = ev.clientY;

        // Touch handling
        if (ev.touches && ev.touches.length > 0) {
            clientX = ev.touches[0].clientX;
            clientY = ev.touches[0].clientY;
        } else if (ev.changedTouches && ev.changedTouches.length > 0) {
            // Needed for touchend
            clientX = ev.changedTouches[0].clientX;
            clientY = ev.changedTouches[0].clientY;
        }

        // Scale factor between CSS pixels and Internal Canvas pixels
        const scaleX = this.canvasEl.nativeElement.width / rect.width;
        const scaleY = this.canvasEl.nativeElement.height / rect.height;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    }

    // HIT TESTING

    // Distance point to point
    dist(x1: number, y1: number, x2: number, y2: number) {
        return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    }

    // Distance point to segment
    distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
        const l2 = (x2 - x1) ** 2 + (y2 - y1) ** 2;
        if (l2 === 0) return this.dist(px, py, x1, y1);
        let t = ((px - x1) * (x2 - x1) + (py - y1) * (y2 - y1)) / l2;
        t = Math.max(0, Math.min(1, t));
        return this.dist(px, py, x1 + t * (x2 - x1), y1 + t * (y2 - y1));
    }

    checkSelection(px: number, py: number): { index: number, handle?: 'start' | 'end' } {
        const w = this.canvasEl.nativeElement.width;
        const h = this.canvasEl.nativeElement.height;
        const HIT_RADIUS = 15;

        // Check handles first (priority) if something is selected
        if (this.selectedElementIndex !== -1) {
            const el = this.elements[this.selectedElementIndex];
            if (el.type === 'line') {
                const sx = el.x * w;
                const sy = el.y * h;
                const ex = el.ex * w;
                const ey = el.ey * h;

                if (this.dist(px, py, sx, sy) < HIT_RADIUS) return { index: this.selectedElementIndex, handle: 'start' };
                if (this.dist(px, py, ex, ey) < HIT_RADIUS) return { index: this.selectedElementIndex, handle: 'end' };
            }
        }

        // Check elements (reverse order for z-index)
        for (let i = this.elements.length - 1; i >= 0; i--) {
            const el = this.elements[i];
            if (el.type === 'line') {
                const d = this.distToSegment(px, py, el.x * w, el.y * h, el.ex * w, el.ey * h);
                if (d < HIT_RADIUS) return { index: i };
            } else if (el.type === 'text') {
                // Simple bounding box approx
                this.ctx.font = "bold 20px Arial";
                const metrics = this.ctx.measureText(el.val);
                const tx = el.x * w;
                const ty = el.y * h;
                // Text draws baseline. Bounds are roughly [tx, ty-20] to [tx+width, ty]
                if (px >= tx && px <= tx + metrics.width && py >= ty - 20 && py <= ty + 5) {
                    return { index: i };
                }
            }
        }

        return { index: -1 };
    }

    handleStart(ev: any) {
        ev.preventDefault();
        const pos = this.getPos(ev);

        // 1. Check selection/hit
        const hit = this.checkSelection(pos.x, pos.y);

        if (hit.index !== -1) {
            this.selectedElementIndex = hit.index;

            this.saveState();

            // Determine interaction
            if (hit.handle === 'start') {
                this.interactionMode = 'resizing_start';
            } else if (hit.handle === 'end') {
                this.interactionMode = 'resizing_end';
            } else {
                this.interactionMode = 'dragging';
            }
            this.startX = pos.x;
            this.startY = pos.y;
            this.render(); // Update highlight
            return;
        }

        // If clicked empty space, deselect
        if (this.selectedElementIndex !== -1) {
            this.selectedElementIndex = -1;
            this.render();
            return;
        }

        // 2. If nothing hit, start drawing (if mode is line)
        if (this.mode === 'line') {
            this.interactionMode = 'drawing';
            this.startX = pos.x;
            this.startY = pos.y;
        }
    }

    handleMove(ev: any) {
        ev.preventDefault();
        if (this.interactionMode === 'none') return;

        const pos = this.getPos(ev);
        const w = this.canvasEl.nativeElement.width;
        const h = this.canvasEl.nativeElement.height;

        if (this.interactionMode === 'drawing') {
            this.render(); // Base + elements
            // Draw preview line
            this.ctx.beginPath();
            this.ctx.moveTo(this.startX, this.startY);
            this.ctx.lineTo(pos.x, pos.y);
            this.ctx.strokeStyle = this.currentColor;
            this.ctx.lineWidth = 3;
            this.ctx.stroke();

        } else if (this.interactionMode === 'dragging') {
            const el = this.elements[this.selectedElementIndex];
            // Delta in NORMALISED units
            const dx = (pos.x - this.startX) / w;
            const dy = (pos.y - this.startY) / h;

            el.x += dx;
            el.y += dy;
            if (el.type === 'line') {
                el.ex += dx;
                el.ey += dy;
            }

            this.startX = pos.x;
            this.startY = pos.y;
            this.render();

        } else if (this.interactionMode === 'resizing_start') {
            const el = this.elements[this.selectedElementIndex];
            el.x = pos.x / w;
            el.y = pos.y / h;
            this.render();

        } else if (this.interactionMode === 'resizing_end') {
            const el = this.elements[this.selectedElementIndex];
            el.ex = pos.x / w;
            el.ey = pos.y / h;
            this.render();
        }
    }

    handleEnd(ev: any) {
        ev.preventDefault();

        if (this.interactionMode === 'drawing') {
            const pos = this.getPos(ev);
            const w = this.canvasEl.nativeElement.width;
            const h = this.canvasEl.nativeElement.height;

            this.saveState(); // Save before adding new line

            // Add line
            this.elements.push({
                type: 'line',
                x: this.startX / w,
                y: this.startY / h,
                ex: pos.x / w,
                ey: pos.y / h,
                color: this.currentColor
            });
            // Auto Select new element
            this.selectedElementIndex = this.elements.length - 1;
            this.mode = 'text'; // Switch to text prompt flow

            // Auto Focus
            setTimeout(() => {
                if (this.textInput) this.textInput.setFocus();
            }, 150);

            this.render();
        }

        this.isDrawing = false;
        this.interactionMode = 'none';
    }

    // History
    history: any[][] = [];

    @ViewChild('textInput') textInput!: any; // IonInput

    saveState() {
        // Deep copy elements to history
        this.history.push(JSON.parse(JSON.stringify(this.elements)));
        // Limit history size?
        if (this.history.length > 20) {
            this.history.shift();
        }
    }

    undo() {
        if (this.history.length > 0) {
            this.elements = this.history.pop()!;
            this.selectedElementIndex = -1;
            this.render();
        }
    }

    // UI Helpers

    deleteSelected() {
        if (this.selectedElementIndex !== -1) {
            this.saveState();
            this.elements.splice(this.selectedElementIndex, 1);
            this.selectedElementIndex = -1;
            this.render();
        }
    }

    async editSelected() {
        if (this.selectedElementIndex !== -1 && this.elements[this.selectedElementIndex].type === 'text') {
            this.textVal = this.elements[this.selectedElementIndex].val;
            this.mode = 'text';
            setTimeout(() => {
                if (this.textInput) this.textInput.setFocus();
            }, 150);
        }
    }

    addText() {
        if (!this.textVal) return;

        // If an element is selected AND it is TEXT, update it instead of adding new
        if (this.selectedElementIndex !== -1 && this.elements[this.selectedElementIndex].type === 'text') {
            this.saveState();
            this.elements[this.selectedElementIndex].val = this.textVal;
            this.elements[this.selectedElementIndex].color = this.currentColor;
            this.textVal = '';
            this.render();
            return;
        }

        const w = this.canvasEl.nativeElement.width;
        const h = this.canvasEl.nativeElement.height;

        // Add text at center or last line pos
        let pixelX = (this.startX > 0) ? this.startX : w / 2;
        let pixelY = (this.startY > 0) ? this.startY : h / 2;

        this.saveState();
        this.elements.push({
            type: 'text',
            val: this.textVal,
            x: pixelX / w,
            y: pixelY / h,
            color: this.currentColor
        });

        this.textVal = '';
        this.selectedElementIndex = this.elements.length - 1;
        this.mode = 'line';
        this.render();
    }

    close() {
        this.modalCtrl.dismiss();
    }

    save() {
        // If user typed text but didn't click OK, add it now
        if (this.mode === 'text' && this.textVal) {
            this.addText();
        }

        // Extract values for logic
        let extractedValues: number[] = [];
        this.elements.forEach(el => {
            if (el.type === 'text') {
                let clean = el.val.replace(',', '.').replace(/[^0-9.]/g, '');
                let val = parseFloat(clean);
                if (!isNaN(val) && val > 0) extractedValues.push(val);
            }
        });

        // We render one last time to ensure everything is burned for the screenshot
        this.render();
        const dataUrl = this.canvasEl.nativeElement.toDataURL('image/jpeg', 0.8);
        const base64 = dataUrl.split(',')[1];

        this.modalCtrl.dismiss({
            base64: base64,
            extractedValues: extractedValues,
            elements: this.elements
        });
    }

}
